import {Component, computed, inject, OnDestroy, signal} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {Router} from '@angular/router';
import {HttpHeaders} from '@angular/common/http';

import {environment} from '../../../environments/environment';
import {Subscription, timer} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {
  GET_INDEX_STATS,
  GET_PROCESS_FAILURES,
  GET_PROCESS_STATS,
  GET_PUBLISH_FAILURES,
  GET_PUBLISH_STATS,
  GET_VTK_JOB_STATS,
} from '../../queries-status';
import {JanusService} from '../../services/janus.service';
import {
  IndexStats,
  ProcessFailure,
  ProcessStats,
  PublishFailure,
  PublishStats,
  StatsApi,
  StatsWindow,
  VtkJobStats,
} from '../../types-status';
import {VTK_STATUS_LABELS} from '../../services/vtk.types';
import {DateComponent} from '../../date/date.component';
import {ProcessLinkComponent} from '../process-link/process-link.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {MonitoringAccessService} from '../../services/monitoring-access.service';

/** How a rate should be read at a glance. Mirrors `sev()` in the Claude Design mock. */
interface Severity {
  /** CSS custom property holding the colour, e.g. `var(--cl-color-ci-brick)`. */
  color: string;
  /** Short uppercase word for the badge. */
  badge: string;
  /** The `cl-color-*` class that colours the badge. */
  badgeClass: string;
}

interface Tile {
  name: string;
  rateText: string;
  rateColor: string;
  sub: string;
  badge: string;
  badgeClass: string;
  accent: string;
}

interface OperationRow {
  action: string;
  started: number;
  failed: number;
  failedColor: string;
  rateText: string;
  rateColor: string;
  denomText: string;
  avgText: string;
  /** Width of the succeeded-or-running part of the volume bar, as a percentage of the busiest row. */
  volPct: number;
  /** Width of the failed part of the same bar. */
  failPct: number;
  hasFailures: boolean;
}

interface Counter {
  label: string;
  value: number;
  color: string;
}

type SortKey = 'failed' | 'rate' | 'volume';

interface VtkSegment {
  label: string;
  count: number;
  color: string;
  pct: number;
}

interface VtkOrgRow {
  urn: string;
  name: string;
  shortUrn: string;
  total: number;
  failed: number;
  failedColor: string;
  segments: VtkSegment[];
}

/**
 * Platform monitoring: process volume and failure rate for both APIs, and VTK jobs across every
 * organization.
 *
 * Implements the `Platform Monitoring.dc.html` Claude Design mock. Two things from that design are
 * load-bearing rather than decorative and must not be simplified away:
 *
 * * **Low-denominator rates are marked, not shouted.** A rate over fewer than
 *   `LOW_CONFIDENCE_THRESHOLD` finished processes reads as "LOW DATA" in neutral steel, because
 *   `previewGenerate` at "50%" is one failure out of two and colouring that like an incident trains
 *   people to ignore the page.
 * * **The volume bar is scaled against the busiest operation in the section**, so a row's bar shows
 *   its share of the section's work. Scaling each bar to its own total would make every row full
 *   width and say nothing.
 *
 * Access is decided by policy alone. The authorizer resolves a caller's policies from the membership
 * matching the X-Castlabs-Organization header, so this page only works in an organization whose role
 * grants `contentplatform:status:Query/*` -- today that is the Content Platform team org, but nothing
 * here names it.
 */
@Component({
  selector: 'app-monitoring',
  standalone: true,
  imports: [DateComponent, ProcessLinkComponent, HeadlineSectionComponent, LogoAreaComponent],
  templateUrl: './monitoring.component.html',
  styleUrl: './monitoring.component.scss',
})
export class MonitoringComponent implements OnDestroy {
  private apollo = inject(Apollo);
  private access = inject(MonitoringAccessService);
  private janus = inject(JanusService);
  private router = inject(Router);

  /**
   * Below this many finished processes a failure rate is statistically meaningless and is reported as
   * low-confidence instead of being colour-coded.
   */
  private static readonly LOW_CONFIDENCE_THRESHOLD = 20;

  private static readonly REFRESH_MS = 30_000;

  /** VTK statuses that mean "did not succeed": Failed, Aborted, Terminated. */
  private static readonly VTK_UNSUCCESSFUL = [3, 4, 6];

  /**
   * Status colour per VTK status number, as CI palette custom properties.
   *
   * Not reused from `vtk.types.ts`: that map holds `cl-color-*` **class** names, which cannot be
   * interpolated into an inline `background` for a chart segment. The labels do come from there, so
   * wording stays consistent with the existing VTK job pages.
   */
  private static readonly VTK_STATUS_COLOR: Record<number, string> = {
    0: 'var(--cl-color-ci-sea)',
    1: 'var(--cl-color-ci-petrol)',
    2: 'var(--cl-color-ci-leaf)',
    3: 'var(--cl-color-ci-brick)',
    4: 'var(--cl-color-ci-steel)',
    5: 'var(--cl-color-ci-clay)',
    6: 'var(--cl-color-ci-ash)',
  };

  /**
   * How many organizations get a row of their own; the rest are folded into a footnote.
   *
   * A row *cap* rather than a minimum job count. The previous rule -- "fewer than 5 jobs goes in the
   * tail" -- was absolute, so it interacted with the reporting window: narrowing to 1 hour pushed
   * genuinely busy organizations under the threshold and the table emptied into a footnote, which reads
   * as the per-organization numbers ignoring the window rather than as a quiet hour. A cap on rows is
   * window-independent: the busiest organizations always get rows, however quiet the period.
   */
  private static readonly MAX_ORG_ROWS = 12;

  readonly windows: {value: StatsWindow; label: string}[] = [
    {value: StatsWindow.Hour_1, label: '1 hour'},
    {value: StatsWindow.Hours_24, label: '24 hours'},
    {value: StatsWindow.Days_7, label: '7 days'},
  ];

  readonly sorts: {key: SortKey; label: string}[] = [
    {key: 'failed', label: 'Most failures'},
    {key: 'rate', label: 'Highest rate'},
    {key: 'volume', label: 'Most volume'},
  ];

  readonly window = signal<StatsWindow>(StatsWindow.Hours_24);

  readonly repository = signal<ProcessStats | null>(null);
  readonly workflow = signal<ProcessStats | null>(null);
  readonly vtk = signal<VtkJobStats | null>(null);
  readonly publish = signal<PublishStats | null>(null);
  readonly index = signal<IndexStats | null>(null);

  readonly loading = signal(true);
  /** Per-section error text, so one failing panel cannot blank the others. */
  readonly errors = signal<Record<string, string | null>>({});
  readonly lastUpdated = signal<Date | null>(null);

  readonly sort = signal<Record<string, SortKey>>({REPOSITORY: 'failed', WORKFLOW: 'failed'});
  readonly focusAction = signal<Record<string, string | null>>({REPOSITORY: null, WORKFLOW: null});
  readonly failures = signal<ProcessFailure[] | null>(null);
  readonly failuresLoading = signal(false);
  readonly failuresNextToken = signal<string | null>(null);

  /** The publish state machine whose failures are open, or null when the methods table shows. */
  readonly publishFocus = signal<string | null>(null);
  readonly publishFailures = signal<PublishFailure[] | null>(null);
  readonly publishFailuresLoading = signal(false);
  readonly publishFailuresNextToken = signal<string | null>(null);

  private readonly organizationNames = signal<Record<string, string>>({});
  private subscriptions = new Subscription();

  readonly StatsApi = StatsApi;
  readonly apis = [StatsApi.Repository, StatsApi.Workflow];

  constructor() {
    // However the user arrived -- login redirect or the nav entry -- this counts as looking at the
    // dashboard, so the next few logins land them wherever they normally would.
    this.access.markViewed();

    this.subscriptions.add(
      this.janus.userinfo$.subscribe({
        next: userinfo => {
          const names: Record<string, string> = {};
          for (const org of JanusService.organizationsOf(userinfo)) {
            names[org.id] = org.legacy_name;
          }
          this.organizationNames.set(names);
        },
        // A missing name map only degrades labels to URNs, so it must not blank the dashboard.
        error: () => this.organizationNames.set({}),
      })
    );

    this.subscriptions.add(this.refresh);
  }

  /**
   * True once the organization probe has finished without finding one that grants monitoring. The
   * page then shows a no-access notice and sends no status query at all.
   */
  readonly noAccess = signal(false);

  /** The 30 s refresh; stopped for good when there is no organization to ask under. */
  private readonly refresh = timer(0, MonitoringComponent.REFRESH_MS).pipe(switchMap(() => this.load())).subscribe();

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ---------------------------------------------------------------- loading

  /**
   * Load all three panels.
   *
   * Settled rather than all-or-nothing: the three queries are independent and the point of a
   * monitoring page is to show what it can. If one stats table is missing -- exactly the state
   * between deploying this app and deploying the rollup to an API -- the others must still render.
   */
  private async load(): Promise<void> {
    // The status queries must go out under the organization that grants monitoring, which the probe
    // decides. Loading before it lands sent the first round with no organization at all on the
    // overlay (no /o/ in the URL): "Not Authorized" on every panel until the next refresh.
    await this.access.resolve();
    // The overlay may have been closed while the probe ran; switchMap cannot cancel this promise.
    if (this.subscriptions.closed) {
      return;
    }
    if (!this.access.organization()) {
      this.noAccess.set(true);
      this.loading.set(false);
      this.refresh.unsubscribe();
      return;
    }
    const window = this.window();
    const results = await Promise.allSettled([
      this.processStats(StatsApi.Repository, window),
      this.processStats(StatsApi.Workflow, window),
      this.vtkStats(window),
      this.publishStats(window),
      this.indexStats(window),
    ]);

    const [repository, workflow, vtk, publish, index] = results;
    if (repository.status === 'fulfilled') {
      this.repository.set(repository.value);
    }
    if (workflow.status === 'fulfilled') {
      this.workflow.set(workflow.value);
    }
    if (vtk.status === 'fulfilled') {
      this.vtk.set(vtk.value);
    }
    if (publish.status === 'fulfilled') {
      this.publish.set(publish.value);
    }
    if (index.status === 'fulfilled') {
      this.index.set(index.value);
    }

    this.errors.set({
      REPOSITORY: MonitoringComponent.reason(repository),
      WORKFLOW: MonitoringComponent.reason(workflow),
      VTK: MonitoringComponent.reason(vtk),
      PUBLISH: MonitoringComponent.reason(publish),
      INDEX: MonitoringComponent.reason(index),
    });
    this.lastUpdated.set(new Date());
    this.loading.set(false);
  }

  private static reason(result: PromiseSettledResult<unknown>): string | null {
    if (result.status !== 'rejected') {
      return null;
    }
    const reason = (result as PromiseRejectedResult).reason;
    return reason instanceof Error ? reason.message : String(reason);
  }

  /**
   * Apollo context pinning the organization for a status query.
   *
   * Required because this renders in the shell's overlay outlet, which deliberately leaves the primary
   * URL alone -- so there may be no `/o/:organization_id` in the URL at all and ContextService would
   * have no organization for the auth middleware to send. The status API resolves a caller's policies
   * from that header, so it has to be the organization that grants monitoring (MonitoringAccessService),
   * not whatever page happens to be underneath.
   */
  private statusContext(): {headers: HttpHeaders} {
    // Only the probed organization, never the navigation's: the page underneath may be in an
    // organization that does not grant monitoring, and monitoring must not change or depend on it.
    const org = this.access.organization();
    if (!org) {
      // load() stops before any query when there is none; a query without the header would be
      // refused by the authorizer anyway, so fail loudly rather than send it.
      throw new Error('No organization grants platform monitoring');
    }
    return {headers: new HttpHeaders().set('X-Castlabs-Organization', org.id)};
  }

  private processStats(api: StatsApi, window: StatsWindow): Promise<ProcessStats> {
    return new Promise((resolve, reject) => {
      this.apollo.use('status').query<{process_stats: ProcessStats}>({
        query: GET_PROCESS_STATS,
        context: this.statusContext(),
        variables: {input: {api, window}},
        fetchPolicy: 'network-only',
      }).subscribe({next: result => resolve(result.data.process_stats), error: reject});
    });
  }

  private publishStats(window: StatsWindow): Promise<PublishStats> {
    return new Promise((resolve, reject) => {
      this.apollo.use('status').query<{publish_stats: PublishStats}>({
        query: GET_PUBLISH_STATS,
        context: this.statusContext(),
        variables: {input: {window}},
        fetchPolicy: 'network-only',
      }).subscribe({next: result => resolve(result.data.publish_stats), error: reject});
    });
  }

  private indexStats(window: StatsWindow): Promise<IndexStats> {
    return new Promise((resolve, reject) => {
      this.apollo.use('status').query<{index_stats: IndexStats}>({
        query: GET_INDEX_STATS,
        context: this.statusContext(),
        variables: {input: {window}},
        fetchPolicy: 'network-only',
      }).subscribe({next: result => resolve(result.data.index_stats), error: reject});
    });
  }

  private vtkStats(window: StatsWindow): Promise<VtkJobStats> {
    return new Promise((resolve, reject) => {
      this.apollo.use('status').query<{vtk_job_stats: VtkJobStats}>({
        query: GET_VTK_JOB_STATS,
        context: this.statusContext(),
        variables: {input: {window}},
        fetchPolicy: 'network-only',
      }).subscribe({next: result => resolve(result.data.vtk_job_stats), error: reject});
    });
  }

  /**
   * Closes the overlay outlet only, exactly like the changelog's Dismiss.
   *
   * The primary outlet keeps whatever the user was on, so dismissing reveals that page rather than
   * navigating away -- and when the login opened this over the organization switcher, dismissing puts
   * the switcher back.
   */
  dismiss(): void {
    this.router.navigate([{outlets: {overlay: null}}], {queryParamsHandling: 'preserve'});
  }

  retry(): void {
    this.loading.set(true);
    this.load();
  }

  selectWindow(window: StatsWindow): void {
    if (window === this.window()) {
      return;
    }
    this.window.set(window);
    this.loading.set(true);
    // Any open drill-down describes the previous window.
    this.focusAction.set({REPOSITORY: null, WORKFLOW: null});
    this.failures.set(null);
    this.clearPublishFocus();
    this.load();
  }

  widenToSevenDays(): void {
    this.selectWindow(StatsWindow.Days_7);
  }

  // ---------------------------------------------------------------- formatting

  private static severity(rate: number | null | undefined, finished: number): Severity {
    if (rate === null || rate === undefined) {
      return {color: 'var(--cl-color-ci-steel)', badge: 'NO DATA', badgeClass: 'cl-color-clay'};
    }
    if (finished < MonitoringComponent.LOW_CONFIDENCE_THRESHOLD) {
      return {color: 'var(--cl-color-ci-steel)', badge: 'LOW DATA', badgeClass: 'cl-color-clay'};
    }
    if (rate >= 0.1) {
      return {color: 'var(--cl-color-ci-brick)', badge: 'ELEVATED', badgeClass: 'cl-color-brick'};
    }
    if (rate >= 0.02) {
      return {color: 'var(--cl-color-ci-honey)', badge: 'WATCH', badgeClass: 'cl-color-honey'};
    }
    return {color: 'var(--cl-color-ci-leaf)', badge: 'NORMAL', badgeClass: 'cl-color-leaf'};
  }

  /** A rate as a percentage, or an em dash when there is no rate to show. */
  percent(rate: number | null | undefined): string {
    return rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(2)}%`;
  }

  /**
   * A duration, scaled to its own magnitude. Values here span seconds to hours in one column, so a
   * single unit would be unreadable at one end or the other.
   */
  duration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) {
      return '—';
    }
    if (ms < 1000) {
      return `${Math.round(ms)} ms`;
    }
    if (ms < 60_000) {
      return `${(ms / 1000).toFixed(1)} s`;
    }
    if (ms < 3_600_000) {
      const minutes = Math.floor(ms / 60_000);
      return `${minutes} m ${Math.round((ms - minutes * 60_000) / 1000)} s`;
    }
    const hours = Math.floor(ms / 3_600_000);
    return `${hours} h ${Math.round((ms - hours * 3_600_000) / 60_000)} m`;
  }

  private static elapsed(from: string, to: string | null | undefined): number | null {
    if (!to) {
      return null;
    }
    const ms = Date.parse(to) - Date.parse(from);
    return Number.isFinite(ms) && ms >= 0 ? ms : null;
  }

  failureDuration(failure: ProcessFailure): string {
    return this.duration(MonitoringComponent.elapsed(failure.start_date, failure.end_date));
  }

  // ---------------------------------------------------------------- section state

  statsFor(api: StatsApi): ProcessStats | null {
    return api === StatsApi.Repository ? this.repository() : this.workflow();
  }

  apiLabel(api: StatsApi): string {
    return api === StatsApi.Repository ? 'Repository processes' : 'Workflow processes';
  }

  errorFor(key: string): string | null {
    return this.errors()[key] ?? null;
  }

  private static finished(stats: ProcessStats): number {
    return stats.totals.succeeded + stats.totals.failed + stats.totals.aborted;
  }

  /** The three headline tiles: one per system, each answering "is this abnormal" alone. */
  readonly tiles = computed<Tile[]>(() => {
    const tiles: Tile[] = [];

    for (const api of this.apis) {
      const key = api === StatsApi.Repository ? 'REPOSITORY' : 'WORKFLOW';
      const name = this.apiLabel(api);
      const stats = this.statsFor(api);

      if (this.errorFor(key)) {
        tiles.push({
          name,
          rateText: '!',
          rateColor: 'var(--cl-color-ci-brick)',
          sub: 'Statistics unavailable — see below',
          badge: 'ERROR',
          badgeClass: 'cl-color-brick',
          accent: 'var(--cl-color-ci-brick)',
        });
        continue;
      }
      if (!stats) {
        tiles.push({
          name,
          rateText: '···',
          rateColor: 'var(--cl-color-ci-clay)',
          sub: 'Loading…',
          badge: 'LOADING',
          badgeClass: 'cl-color-clay',
          accent: 'var(--cl-color-ci-clay)',
        });
        continue;
      }

      const finished = MonitoringComponent.finished(stats);
      if (stats.totals.started === 0) {
        tiles.push({
          name,
          rateText: '—',
          rateColor: 'var(--cl-color-ci-steel)',
          sub: 'No processes in this window',
          badge: 'EMPTY',
          badgeClass: 'cl-color-clay',
          accent: 'var(--cl-color-ci-clay)',
        });
        continue;
      }
      if (finished === 0) {
        tiles.push({
          name,
          rateText: '—',
          rateColor: 'var(--cl-color-ci-steel)',
          sub: `Nothing has finished yet · ${stats.totals.in_progress} in progress`,
          badge: 'NO DATA',
          badgeClass: 'cl-color-clay',
          accent: 'var(--cl-color-ci-clay)',
        });
        continue;
      }

      const severity = MonitoringComponent.severity(stats.totals.failure_rate, finished);
      tiles.push({
        name,
        rateText: this.percent(stats.totals.failure_rate),
        rateColor: severity.color,
        sub: `${stats.totals.failed} failed of ${finished} finished · ${stats.totals.started} started`,
        badge: severity.badge,
        badgeClass: severity.badgeClass,
        accent: severity.color,
      });
    }

    tiles.push(this.publishTile());
    tiles.push(this.vtkTile());
    tiles.push(this.indexTile());
    return tiles;
  });

  /**
   * How far behind S3 the search index is, as one number.
   *
   * Lag rather than volume, because that is what a user notices: an index that is hours behind
   * looks to them like a file that "did not upload". Document counts and queue depth explain the
   * lag, they are not the headline. A backlog with no lag yet is still healthy.
   */
  private indexTile(): Tile {
    const name = 'Search index';
    if (this.errorFor('INDEX')) {
      return {
        name,
        rateText: '!',
        rateColor: 'var(--cl-color-ci-brick)',
        sub: 'Statistics unavailable — see below',
        badge: 'ERROR',
        badgeClass: 'cl-color-brick',
        accent: 'var(--cl-color-ci-brick)',
      };
    }
    const stats = this.index();
    if (!stats) {
      return {
        name,
        rateText: '···',
        rateColor: 'var(--cl-color-ci-clay)',
        sub: 'Loading…',
        badge: 'LOADING',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }

    // A dropped message is the one state here that no amount of waiting repairs: the object is in
    // S3, the index does not have it, and nothing will retry. It outranks a large lag.
    if (stats.dlq_depth > 0) {
      return {
        name,
        rateText: String(stats.dlq_depth),
        rateColor: 'var(--cl-color-ci-brick)',
        sub: `${stats.dlq_depth} dropped indexing messages — needs a redrive`,
        badge: 'DLQ',
        badgeClass: 'cl-color-brick',
        accent: 'var(--cl-color-ci-brick)',
      };
    }

    if (stats.lag_seconds === null || stats.lag_seconds === undefined) {
      return {
        name,
        rateText: '—',
        rateColor: 'var(--cl-color-ci-steel)',
        sub: stats.documents ? 'Index unreadable' : 'Index empty',
        badge: 'NO DATA',
        badgeClass: 'cl-color-steel',
        accent: 'var(--cl-color-ci-steel)',
      };
    }

    // An index that is perfectly current but answers every search with nothing is not healthy, and the
    // lag number alone says it is. This is the case the tile missed entirely.
    if (this.indexZeroResultSevere()) {
      return {
        name,
        rateText: `${Math.round((stats.search.zero_result_rate ?? 0) * 100)}%`,
        rateColor: 'var(--cl-color-ci-brick)',
        sub: 'of searches return nothing — index is current but not answering',
        badge: 'NO RESULTS',
        badgeClass: 'cl-color-brick',
        accent: 'var(--cl-color-ci-brick)',
      };
    }

    const scanning = stats.scan?.status === 'RUNNING';
    const severity = MonitoringComponent.indexSeverity(stats.lag_seconds, scanning);
    const queued = stats.queue_depth + stats.queue_in_flight;
    return {
      name,
      rateText: MonitoringComponent.duration(stats.lag_seconds),
      rateColor: severity.color,
      sub: [
        'behind S3',
        queued > 0 ? `${queued.toLocaleString()} queued` : null,
        scanning ? (stats.scan?.rebuild ? 'rebuild running' : 'full scan running') : null,
      ].filter(Boolean).join(' · '),
      badge: severity.badge,
      badgeClass: severity.badgeClass,
      accent: severity.color,
    };
  }

  /**
   * Lag thresholds, relaxed while a scan runs.
   *
   * A full scan republishes every object in every bucket through the same queue, so hours of
   * backlog during one is the expected state rather than a fault -- the same reason the backlog-age
   * alarm on the queue sits at 12 hours. Grading a scan against the steady-state thresholds would
   * paint the board red every time one runs and teach people to ignore it.
   */
  private static indexSeverity(lagSeconds: number, scanning: boolean):
      {color: string; badge: string; badgeClass: string} {
    const warn = scanning ? 6 * 3600 : 900;
    const bad = scanning ? 18 * 3600 : 3600;
    if (lagSeconds >= bad) {
      return {color: 'var(--cl-color-ci-brick)', badge: 'STALE', badgeClass: 'cl-color-brick'};
    }
    if (lagSeconds >= warn) {
      return {color: 'var(--cl-color-ci-honey)', badge: 'BEHIND', badgeClass: 'cl-color-honey'};
    }
    return {color: 'var(--cl-color-ci-leaf)', badge: 'CURRENT', badgeClass: 'cl-color-leaf'};
  }

  /** The lag headline, formatted, for the table row. */
  indexLag(): string {
    const seconds = this.index()?.lag_seconds;
    return seconds === null || seconds === undefined ? '—' : MonitoringComponent.duration(seconds);
  }

  /**
   * Whether the zero-result rate is high enough to be a finding rather than a mood.
   *
   * 90%, and only once enough searches have been answered for the rate to mean anything: three empty
   * searches out of three is a plausible morning, not a fault, and a panel that shouts at that gets
   * ignored by the time it matters.
   */
  indexZeroResultSevere(): boolean {
    const search = this.index()?.search;
    if (!search || search.zero_result_rate === null || search.zero_result_rate === undefined) {
      return false;
    }
    return search.zero_result_rate >= 0.9 && search.zero_results >= 20;
  }

  indexZeroResultColor(): string {
    const rate = this.index()?.search?.zero_result_rate;
    if (rate === null || rate === undefined) {
      return 'var(--cl-color-ci-steel)';
    }
    if (this.indexZeroResultSevere()) {
      return 'var(--cl-color-ci-brick)';
    }
    return rate >= 0.6 ? 'var(--cl-color-ci-honey)' : 'var(--cl-color-ci-night)';
  }

  /** Age of the oldest queued message, formatted. */
  indexQueueAge(): string {
    const seconds = this.index()?.queue_oldest_seconds;
    return seconds === null || seconds === undefined ? '—' : MonitoringComponent.duration(seconds);
  }

  /** Seconds as the coarsest unit that still reads exactly, e.g. `3 min`, `4.2 h`. */
  static duration(seconds: number): string {
    if (seconds < 90) {
      return `${seconds} s`;
    }
    if (seconds < 5400) {
      return `${Math.round(seconds / 60)} min`;
    }
    return `${(seconds / 3600).toFixed(1)} h`;
  }

  /** Everything the publication methods delivered in the window, folded into one answer. */
  private publishTile(): Tile {
    const name = 'Publication methods';
    if (this.errorFor('PUBLISH')) {
      return {
        name,
        rateText: '!',
        rateColor: 'var(--cl-color-ci-brick)',
        sub: 'Statistics unavailable — see below',
        badge: 'ERROR',
        badgeClass: 'cl-color-brick',
        accent: 'var(--cl-color-ci-brick)',
      };
    }
    const stats = this.publish();
    if (!stats) {
      return {
        name,
        rateText: '···',
        rateColor: 'var(--cl-color-ci-clay)',
        sub: 'Loading…',
        badge: 'LOADING',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }

    const started = stats.methods.reduce((total, method) => total + method.started, 0);
    const failed = stats.methods.reduce((total, method) => total + method.failed, 0);
    const finished = stats.methods.reduce(
      (total, method) => total + method.succeeded + method.failed + method.aborted, 0);
    if (started === 0) {
      return {
        name,
        rateText: '—',
        rateColor: 'var(--cl-color-ci-steel)',
        sub: `No publishes in this window · ${stats.methods.length} methods configured`,
        badge: 'EMPTY',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }
    if (finished === 0) {
      return {
        name,
        rateText: '—',
        rateColor: 'var(--cl-color-ci-steel)',
        sub: `Nothing has finished yet · ${started} in flight`,
        badge: 'NO DATA',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }

    const rate = failed / finished;
    const severity = MonitoringComponent.severity(rate, finished);
    return {
      name,
      rateText: this.percent(rate),
      rateColor: severity.color,
      sub: `${failed} failed of ${finished} finished · ${stats.methods.length} methods`,
      badge: severity.badge,
      badgeClass: severity.badgeClass,
      accent: severity.color,
    };
  }

  private vtkTile(): Tile {
    const name = 'VTK encoding';
    if (this.errorFor('VTK')) {
      return {
        name,
        rateText: '!',
        rateColor: 'var(--cl-color-ci-brick)',
        sub: 'Snapshot unavailable — see below',
        badge: 'ERROR',
        badgeClass: 'cl-color-brick',
        accent: 'var(--cl-color-ci-brick)',
      };
    }
    const stats = this.vtk();
    if (!stats) {
      return {
        name,
        rateText: '···',
        rateColor: 'var(--cl-color-ci-clay)',
        sub: 'Loading…',
        badge: 'LOADING',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }
    if (stats.total === 0) {
      return {
        name,
        rateText: '—',
        rateColor: 'var(--cl-color-ci-steel)',
        sub: 'No jobs in this window',
        badge: 'EMPTY',
        badgeClass: 'cl-color-clay',
        accent: 'var(--cl-color-ci-clay)',
      };
    }

    const unsuccessful = this.vtkUnsuccessfulCount();
    const rate = unsuccessful / stats.total;
    const severity = MonitoringComponent.severity(rate, stats.total);
    return {
      name,
      rateText: this.percent(rate),
      rateColor: severity.color,
      sub: `${unsuccessful} failed of ${stats.total} jobs · ${stats.by_organization.length} organizations`,
      badge: severity.badge,
      badgeClass: severity.badgeClass,
      accent: severity.color,
    };
  }

  counters(api: StatsApi): Counter[] {
    const stats = this.statsFor(api);
    if (!stats) {
      return [];
    }
    const t = stats.totals;
    return [
      {label: 'Started', value: t.started, color: 'var(--cl-color-text)'},
      {label: 'Succeeded', value: t.succeeded, color: 'var(--cl-color-text)'},
      {label: 'Failed', value: t.failed, color: t.failed > 0 ? 'var(--cl-color-ci-brick)' : 'var(--cl-color-text)'},
      {label: 'Aborted', value: t.aborted, color: 'var(--cl-color-text-faded)'},
      {label: 'In progress', value: t.in_progress, color: 'var(--cl-color-text-faded)'},
    ];
  }

  sortKey(api: StatsApi): SortKey {
    return this.sort()[this.keyFor(api)];
  }

  setSort(api: StatsApi, key: SortKey): void {
    this.sort.update(current => ({...current, [this.keyFor(api)]: key}));
  }

  keyFor(api: StatsApi): string {
    return api === StatsApi.Repository ? 'REPOSITORY' : 'WORKFLOW';
  }

  /** Per-operation rows, sorted by the section's current sort and scaled for the volume bar. */
  rows(api: StatsApi): OperationRow[] {
    const stats = this.statsFor(api);
    if (!stats) {
      return [];
    }

    const key = this.sortKey(api);
    const sorted = [...stats.by_action].sort((a, b) => {
      if (key === 'rate') {
        return (b.counts.failure_rate ?? -1) - (a.counts.failure_rate ?? -1);
      }
      if (key === 'volume') {
        return b.counts.started - a.counts.started;
      }
      return b.counts.failed - a.counts.failed || b.counts.started - a.counts.started;
    });

    const busiest = Math.max(1, ...stats.by_action.map(entry => entry.counts.started));

    return sorted.map(entry => {
      const c = entry.counts;
      const finished = c.succeeded + c.failed + c.aborted;
      const severity = MonitoringComponent.severity(c.failure_rate, finished);
      return {
        action: entry.action,
        started: c.started,
        failed: c.failed,
        failedColor: c.failed > 0 ? 'var(--cl-color-ci-brick)' : 'var(--cl-color-text-faded)',
        rateText: this.percent(c.failure_rate),
        rateColor: severity.color,
        denomText: finished ? `${c.failed} of ${finished} finished` : 'nothing finished yet',
        avgText: this.duration(c.avg_duration_ms),
        // A busy row keeps a visible sliver even at 0 failures so the bar never disappears.
        volPct: Math.max(2, ((c.started - c.failed) / busiest) * 100),
        failPct: (c.failed / busiest) * 100,
        hasFailures: c.failed > 0,
      };
    });
  }

  showTable(api: StatsApi): boolean {
    const key = this.keyFor(api);
    const stats = this.statsFor(api);
    return !this.errorFor(key) && !this.focusAction()[key] && !!stats && stats.by_action.length > 0;
  }

  isEmpty(api: StatsApi): boolean {
    const stats = this.statsFor(api);
    return !this.errorFor(this.keyFor(api)) && !!stats && stats.totals.started === 0;
  }

  staleCount(api: StatsApi): number {
    return this.statsFor(api)?.totals.stale_in_progress ?? 0;
  }

  // ---------------------------------------------------------------- failure drill-down

  focusedAction(api: StatsApi): string | null {
    return this.focusAction()[this.keyFor(api)];
  }

  focusSummary(api: StatsApi): string {
    const action = this.focusedAction(api);
    const row = this.rows(api).find(entry => entry.action === action);
    if (!row) {
      return '';
    }
    return `${row.failed} failures · ${row.rateText} of ${row.started} started`;
  }

  viewFailures(api: StatsApi, action: string): void {
    this.focusAction.update(current => ({...current, [this.keyFor(api)]: action}));
    this.failures.set(null);
    this.failuresNextToken.set(null);
    this.loadFailures(api, action, null);
  }

  loadMoreFailures(api: StatsApi): void {
    const action = this.focusedAction(api);
    if (action) {
      this.loadFailures(api, action, this.failuresNextToken());
    }
  }

  private loadFailures(api: StatsApi, action: string, nextToken: string | null): void {
    this.failuresLoading.set(true);
    this.apollo.use('status').query<{process_failures: {failures: ProcessFailure[]; next_token: string | null}}>({
      query: GET_PROCESS_FAILURES,
      context: this.statusContext(),
      variables: {input: {api, window: this.window(), action, limit: 50, next_token: nextToken}},
      fetchPolicy: 'network-only',
    }).subscribe({
      next: result => {
        const page = result.data.process_failures;
        this.failures.update(current => (current && nextToken ? [...current, ...page.failures] : page.failures));
        this.failuresNextToken.set(page.next_token ?? null);
        this.failuresLoading.set(false);
      },
      error: error => {
        this.errors.update(current => ({
          ...current,
          [this.keyFor(api)]: error instanceof Error ? error.message : String(error),
        }));
        this.failuresLoading.set(false);
      },
    });
  }

  clearFocus(api: StatsApi): void {
    this.focusAction.update(current => ({...current, [this.keyFor(api)]: null}));
    this.failures.set(null);
    this.failuresNextToken.set(null);
  }

  // ---------------------------------------------------------------- publication methods

  /**
   * Per-method rows in the API's order (most failures first), scaled for the volume bar like the
   * process sections. `action` carries the method label; `stateMachine` is the drill-down key.
   */
  readonly publishRows = computed<(OperationRow & {stateMachine: string})[]>(() => {
    const stats = this.publish();
    if (!stats) {
      return [];
    }
    const busiest = Math.max(1, ...stats.methods.map(method => method.started));
    return stats.methods.map(method => {
      const finished = method.succeeded + method.failed + method.aborted;
      const severity = MonitoringComponent.severity(method.failure_rate, finished);
      return {
        action: method.method,
        stateMachine: method.state_machine,
        started: method.started,
        failed: method.failed,
        failedColor: method.failed > 0 ? 'var(--cl-color-ci-brick)' : 'var(--cl-color-text-faded)',
        rateText: this.percent(method.failure_rate),
        rateColor: severity.color,
        denomText: finished ? `${method.failed} of ${finished} finished` : 'nothing finished yet',
        avgText: this.duration(method.avg_duration_ms),
        volPct: Math.max(2, ((method.started - method.failed) / busiest) * 100),
        failPct: (method.failed / busiest) * 100,
        hasFailures: method.failed > 0,
      };
    });
  });

  showPublishTable(): boolean {
    const stats = this.publish();
    return !this.errorFor('PUBLISH') && !this.publishFocus() && !!stats && stats.methods.length > 0;
  }

  publishIsEmpty(): boolean {
    const stats = this.publish();
    return !this.errorFor('PUBLISH') && !!stats
      && stats.methods.every(method => method.started === 0);
  }

  publishFocusSummary(): string {
    const machine = this.publishFocus();
    const row = this.publishRows().find(entry => entry.stateMachine === machine);
    return row ? `${row.failed} failures · ${row.rateText} of ${row.started} started` : '';
  }

  viewPublishFailures(stateMachine: string): void {
    this.publishFocus.set(stateMachine);
    this.publishFailures.set(null);
    this.publishFailuresNextToken.set(null);
    this.loadPublishFailures(stateMachine, null);
  }

  loadMorePublishFailures(): void {
    const machine = this.publishFocus();
    if (machine) {
      this.loadPublishFailures(machine, this.publishFailuresNextToken());
    }
  }

  private loadPublishFailures(stateMachine: string, nextToken: string | null): void {
    this.publishFailuresLoading.set(true);
    this.apollo.use('status').query<{publish_failures: {failures: PublishFailure[]; next_token: string | null}}>({
      query: GET_PUBLISH_FAILURES,
      context: this.statusContext(),
      variables: {input: {state_machine: stateMachine, window: this.window(), limit: 50, next_token: nextToken}},
      fetchPolicy: 'network-only',
    }).subscribe({
      next: result => {
        const page = result.data.publish_failures;
        this.publishFailures.update(current =>
          (current && nextToken ? [...current, ...page.failures] : page.failures));
        this.publishFailuresNextToken.set(page.next_token ?? null);
        this.publishFailuresLoading.set(false);
      },
      error: error => {
        this.errors.update(current => ({
          ...current,
          PUBLISH: error instanceof Error ? error.message : String(error),
        }));
        this.publishFailuresLoading.set(false);
      },
    });
  }

  clearPublishFocus(): void {
    this.publishFocus.set(null);
    this.publishFailures.set(null);
    this.publishFailuresNextToken.set(null);
  }

  publishFailureDuration(failure: PublishFailure): string {
    return this.duration(MonitoringComponent.elapsed(failure.start_date, failure.end_date));
  }

  // ---------------------------------------------------------------- VTK

  /**
   * A deep link into the VTK app's job list for one organization.
   *
   * Same shape the PO item pages use for a single job -- `?as=` is the organization the viewer is
   * acting as, which VTK needs because the monitoring page is opened under a different org than the
   * one that owns the jobs.
   */
  vtkAppUrl(organizationUrn: string): string {
    // The organization this page acts as -- the one the status queries go out under.
    const actingAs = this.access.organization()?.id ?? '';
    return `${environment.VTK}/#/o/${organizationUrn}/jobs?as=${actingAs}`;
  }

  vtkStatusLabel(status: number | null | undefined, fallback: string): string {
    return status === null || status === undefined ? fallback : VTK_STATUS_LABELS[status] ?? fallback;
  }

  vtkStatusColor(status: number | null | undefined): string {
    return status === null || status === undefined
      ? 'var(--cl-color-ci-ash)'
      : MonitoringComponent.VTK_STATUS_COLOR[status] ?? 'var(--cl-color-ci-ash)';
  }

  private vtkUnsuccessfulCount(): number {
    return (this.vtk()?.by_status ?? [])
      .filter(entry => entry.status !== null && MonitoringComponent.VTK_UNSUCCESSFUL.includes(entry.status))
      .reduce((total, entry) => total + entry.count, 0);
  }

  /** The overall status distribution, as bar segments with a legend. */
  readonly vtkSegments = computed<VtkSegment[]>(() => {
    const stats = this.vtk();
    if (!stats || !stats.total) {
      return [];
    }
    return stats.by_status.map(entry => ({
      label: this.vtkStatusLabel(entry.status, entry.status_text),
      count: entry.count,
      color: this.vtkStatusColor(entry.status),
      pct: (entry.count / stats.total) * 100,
    }));
  });

  /** The busiest organizations, busiest first. The API already sorts them, so this only trims. */
  readonly vtkOrganizations = computed<VtkOrgRow[]>(() => {
    const stats = this.vtk();
    if (!stats) {
      return [];
    }
    return stats.by_organization
      .slice(0, MonitoringComponent.MAX_ORG_ROWS)
      .map(org => {
        const failed = org.by_status
          .filter(entry => entry.status !== null && MonitoringComponent.VTK_UNSUCCESSFUL.includes(entry.status))
          .reduce((total, entry) => total + entry.count, 0);
        return {
          urn: org.organization_urn,
          name: this.organizationNames()[org.organization_urn] ?? org.organization_urn,
          shortUrn: org.organization_urn,
          total: org.total,
          failed,
          failedColor: failed > 0 ? 'var(--cl-color-ci-brick)' : 'var(--cl-color-text-faded)',
          segments: org.by_status.map(entry => ({
            label: this.vtkStatusLabel(entry.status, entry.status_text),
            count: entry.count,
            color: this.vtkStatusColor(entry.status),
            pct: (entry.count / org.total) * 100,
          })),
        };
      });
  });

  /**
   * The organizations past the row cap, as a sentence. A long list buries the ones that matter, but
   * dropping them silently would misrepresent the totals -- so they are stated, not hidden.
   */
  readonly vtkTailNote = computed<string | null>(() => {
    const tail = (this.vtk()?.by_organization ?? []).slice(MonitoringComponent.MAX_ORG_ROWS);
    if (!tail.length) {
      return null;
    }
    const jobs = tail.reduce((total, org) => total + org.total, 0);
    const failed = tail.reduce(
      (total, org) =>
        total +
        org.by_status
          .filter(entry => entry.status !== null && MonitoringComponent.VTK_UNSUCCESSFUL.includes(entry.status))
          .reduce((sum, entry) => sum + entry.count, 0),
      0
    );
    const plural = tail.length === 1 ? 'organization' : 'organizations';
    const verb = tail.length === 1 ? 'is' : 'are';
    return `${tail.length} quieter ${plural} beyond the busiest ${MonitoringComponent.MAX_ORG_ROWS} ${verb} collapsed (${jobs} jobs total, ${failed} failed).`;
  });

  /**
   * The period the VTK numbers on screen actually describe, taken from the VTK response itself.
   *
   * Deliberately not the header's window: that is computed from the process stats, so if the VTK query
   * fails while the others succeed, the panel keeps the previous window's figures under a header
   * claiming the newly selected one -- indistinguishable from fresh data. Reading window_start and
   * window_end off the same object the numbers came from makes a stale panel self-evident.
   */
  readonly vtkWindow = computed<{start: string, end: string} | null>(() => {
    const stats = this.vtk();
    return stats ? {start: stats.window_start, end: stats.window_end} : null;
  });

  vtkIsEmpty(): boolean {
    const stats = this.vtk();
    return !this.errorFor('VTK') && !!stats && stats.total === 0;
  }

  // ---------------------------------------------------------------- header

  /** Start of the reporting window shown in the header; null before the first answer. */
  readonly windowStart = computed<string | null>(() => (this.repository() ?? this.workflow())?.window_start ?? null);

  readonly updatedLabel = computed(() => {
    if (this.loading()) {
      return 'Refreshing…';
    }
    const at = this.lastUpdated();
    if (!at) {
      return 'auto-refresh every 30 s';
    }
    const seconds = Math.max(0, Math.round((Date.now() - at.getTime()) / 1000));
    return `Updated ${seconds} s ago · auto-refresh every 30 s`;
  });
}
